import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, ButtonPro, LogoSVG } from 'components/atoms';
import { AdminHeader, AdminSidebar } from 'components';
import { EmployeeReviewKPICards } from 'components/organisms/EmployeeReviewKPICards';
import { EmployeeReviewTable } from 'components/organisms/EmployeeReviewTable';
import { EmployeeReviewFilter } from 'components/organisms/EmployeeReviewFilter';
import { QuickActions } from 'components/organisms/QuickActions';
import { UpcomingDeadlines } from 'components/organisms/UpcomingDeadlines';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { getAdminEmployeeInfo } from 'store/Admin';
import { AdminEmployeeInfo } from 'models/models';
import './index.scss';

export interface EmployeeReviewData {
  id: string;
  name: string;
  nextReviewDate?: string;
  percentage: number;
  status: 'pending' | 'approved' | 'ongoing' | 'completed' | 'incomplete';
  team?: string;
  role?: string;
  avatar?: string;
  userRoleId: string;
  upn: string;
  epfNumber: string;
  designation?: string | null;
  employeeNickName: string;
  isActive: boolean;
  acceptanceStatus?: 'accepted' | 'declined' | 'pending' | 'mixed';
  goalDetails?: {
    goalTitle: string;
    acceptanceStatus: 'Accepted' | 'Declined' | 'Pending';
  }[];
  performance?: {
    totalGoals: number;
    completedGoals: number;
    pendingGoals: number;
    inProgressGoals: number;
    acceptedGoals: number;
    declinedGoals: number;
    totalSubtasks: number;
    completedSubtasks: number;
    pendingSubtasks: number;
    inProgressSubtasks: number;
    goalsCompletionRate: number;
    subtasksCompletionRate: number;
    overallPerformanceRate: number;
  };
}

export const EmployeeReview = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmployeeTable, setShowEmployeeTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const employeeData = useAppSelector((state: RootState) => {
    return state.admin.employeeData.filter((employee) => employee.isActive);
  });

  const transformEmployeeData = (
    employees: AdminEmployeeInfo[]
  ): EmployeeReviewData[] => {
    return employees.map((employee) => ({
      id: employee.epfNumber || employee.userRoleId,
      name: employee.employeeNickName || employee.employeeFullName,
      nextReviewDate: '',
      percentage: 0,
      status: 'pending' as
        | 'pending'
        | 'approved'
        | 'ongoing'
        | 'completed'
        | 'incomplete',
      team: '',
      role: employee.designation || employee.role,
      avatar: employee.employeeNickName
        ? employee.employeeNickName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
        : 'NA',
      userRoleId: employee.userRoleId,
      upn: employee.upn,
      epfNumber: employee.epfNumber,
      designation: employee.designationName,
      employeeNickName: employee.employeeNickName,
      isActive: employee.isActive as boolean
    }));
  };

  const employeeReviews = useMemo(
    () => transformEmployeeData(employeeData),
    [employeeData]
  );

  const filteredEmployees = useMemo(() => {
    if (searchTerm.trim() === '') {
      return employeeReviews;
    } else {
      return employeeReviews.filter(
        (employee) =>
          employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.epfNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          employee.upn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employeeNickName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          employee.designation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  }, [employeeReviews, searchTerm]);

  const kpiData = {
    totalEmployees: employeeReviews.length,
    activeGoals: 0,
    completedGoals: 0,
    avgProgress: 0,
    pendingReviews: employeeReviews.length
  };

  const upcomingDeadlines: Array<{
    id: string;
    title: string;
    date: string;
    priority: 'high' | 'medium' | 'low';
    type: string;
  }> = [];

  const handleEmployeeClick = (employee: EmployeeReviewData) => {
    navigate(`/admin/employee-review/${employee.userRoleId}/goals`, {
      state: { employee }
    });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handleMyGoalsClick = () => {
    setShowEmployeeTable(true);
  };

  const handleScheduleReviewClick = () => {
    navigate('/admin/schedule-review');
  };

  const handleBackToDashboard = () => {
    setShowEmployeeTable(false);
  };

  useEffect(() => {
    if (employeeData.length === 0) {
      setIsLoading(true);
      dispatch(getAdminEmployeeInfo()).finally(() => {
        setIsLoading(false);
      });
    }
  }, [dispatch, employeeData.length]);

  if (isLoading) {
    return (
      <>
        <AdminHeader />
        <div className="dashboard-wrapper">
          <AdminSidebar />
          <div className="employee-review-main-wrapper">
            <div className="loading-container">
              <LogoSVG />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (showEmployeeTable) {
    return (
      <>
        <AdminHeader />
        <div className="dashboard-wrapper">
          <AdminSidebar />
          <div className="employee-review-main-wrapper">
            <div className="back-button-container">
              <ButtonPro
                label="Back to Dashboard"
                onClick={handleBackToDashboard}
                type="white"
                icon="ri-arrow-left-line"
              />
            </div>
            <div className="employee-review-header">
              <div className="header-content">
                <div className="header-with-back">
                  <div className="header-text">
                    <Typography
                      type="heading4"
                      text="Employee Goals Management"
                      weight="bold"
                    />
                    <Typography
                      type="small_regular"
                      text="Manage and assign goals to all employees"
                      color="gray"
                    />
                  </div>
                </div>
              </div>

              <EmployeeReviewFilter 
                onSearch={handleSearch} 
                searchTerm={searchTerm}
              />
            </div>

            <div className="employee-review-content">
              <div className="table-container">
                <EmployeeReviewTable
                  data={paginatedEmployees}
                  onEmployeeClick={handleEmployeeClick}
                  showGoalsColumn={true}
                  showPerformanceColumn={false}
                  showStatusColumn={false}
                  showNextReviewColumn={false}
                  showPagination={true}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredEmployees.length}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminHeader />
      <div className="dashboard-wrapper">
        <AdminSidebar />
        <div className="employee-review-main-wrapper">
          <div className="page-header">
            <div className="employee-review-header">
              <div className="header-content">
                <div className="header-text">
                  <Typography
                    type="heading4"
                    text="Employee Goals Dashboard"
                    weight="bold"
                  />
                  <Typography
                    type="small_regular"
                    text="Monitor performance, goals, and team progress"
                    color="gray"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-content">
            <div className="kpi-section">
              <EmployeeReviewKPICards
                data={employeeReviews}
                kpiData={kpiData}
              />
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-left">
                <div className="quick-actions-container">
                  <QuickActions
                    onMyGoalsClick={handleMyGoalsClick}
                    onScheduleReviewClick={handleScheduleReviewClick}
                  />
                </div>
              </div>

              <div className="dashboard-right">
                <div className="upcoming-deadlines-container">
                  <UpcomingDeadlines deadlines={upcomingDeadlines} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
